import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Select, Spin } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import { get_inviteMembersV2, get_inviteVipLevelsV2 } from "@/api/referral";
import { pickRfapiPagedList } from "@/components/Pages/referral/utils/pickRfapiPagedList";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { Big, getUrl } = Util;

const PAGE_SIZE = 20;
const SCROLL_LOAD_EDGE = 48;

/** 预览长内容：访问 /en/referral?mockMembers=1 */
const MOCK_MEMBER_ID = "9759260999754";

function getMockMembersRows() {
  return [
    {
      key: "mock-1",
      cursorId: "",
      userId: MOCK_MEMBER_ID,
      superiorLabel: MOCK_MEMBER_ID,
      levelNum: 1,
      rebate: "9876543210.123456789012345678901234567890",
    },
    {
      key: "mock-2",
      cursorId: "",
      userId: MOCK_MEMBER_ID,
      superiorLabel: MOCK_MEMBER_ID,
      levelNum: 12,
      rebate: "99999999999999999999.999999999999999999",
    },
    {
      key: "mock-3",
      cursorId: "",
      userId: MOCK_MEMBER_ID,
      superiorLabel: MOCK_MEMBER_ID,
      levelNum: 3,
      rebate: "0.0000000000000000000000000001",
    },
  ];
}

function readMockMembersFromUrl() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("mockMembers") === "1";
  } catch {
    return false;
  }
}

function SearchIcon() {
  return (
    <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M9.5 9.5L12 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function mapItems(items) {
  return (items || []).map((record, index) => {
    const downlineUserId =
      record.downlineUserId ?? record.userId ?? record.subUserId ?? record.downline_user_id;
    const cursorId = record.id ?? record.cursorId ?? record.downlineId;
    const directParent =
      record.directParentUserId ?? record.parentUserId ?? record.direct_parent_user_id;
    const layer = record.inviteLayer ?? record.layer ?? record.invite_layer;
    const rebate =
      record.totalCommissionAmountU ??
      record.totalCommissionAmount ??
      record.commissionAmountU ??
      record.total_commission_amount_u;

    return {
      key: downlineUserId != null ? String(downlineUserId) : `row-${index}`,
      cursorId: cursorId != null && cursorId !== "" ? String(cursorId) : "",
      userId: downlineUserId != null ? String(downlineUserId) : "—",
      superiorLabel: directParent != null ? String(directParent) : "—",
      levelNum: layer != null ? Number(layer) : 0,
      rebate,
    };
  });
}

export default function Members() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus } = appState;

  const [loginQuery, setLoginQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [levelFilter, setLevelFilter] = useState("all");
  const [vipLevels, setVipLevels] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchKey, setSearchKey] = useState("");

  const loadingMoreLock = useRef(false);
  const tableBodyRef = useRef(null);
  const scrollSentinelRef = useRef(null);
  const loadMoreRef = useRef(() => {});

  useEffect(() => {
    setLoginQuery("?backurl=" + encodeURIComponent(typeof window !== "undefined" ? window.location.href : ""));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearchKey(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get_inviteVipLevelsV2();
        if (cancelled) return;
        const list = Array.isArray(res?.result) ? res.result : Array.isArray(res) ? res : [];
        setVipLevels(
          [...list].sort((a, b) => Number(a?.vipLevel || 0) - Number(b?.vipLevel || 0))
        );
      } catch {
        if (!cancelled) {
          setVipLevels([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildParams = useCallback(
    (fromId) => {
      const levelParam = levelFilter === "all" ? undefined : Number(levelFilter);
      const sk = (searchKey || "").trim();
      const p = {
        direction: "NEXT",
        limit: PAGE_SIZE,
      };
      if (levelParam != null && !Number.isNaN(levelParam)) {
        p.inviteLayer = levelParam;
      }
      if (fromId !== undefined && fromId !== null && fromId !== "") {
        p.fromId = fromId;
      }
      if (sk.length > 0) {
        p.downlineUserId = sk;
      }
      return p;
    },
    [levelFilter, searchKey]
  );

  /** 筛选 / 搜索变化：重新拉第一页 */
  useEffect(() => {
    if (loginStatus != 1) return;
    if (readMockMembersFromUrl()) {
      setLoading(true);
      setHasMore(false);
      setRows(getMockMembersRows());
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setHasMore(false);
      try {
        const res = await get_inviteMembersV2({ params: buildParams() });
        if (cancelled) return;
        const { items: list, hasNext } = pickRfapiPagedList(res);
        setHasMore(hasNext);
        setRows(mapItems(list));
      } catch {
        if (!cancelled) {
          setRows([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loginStatus, levelFilter, searchKey, buildParams]);

  const loadMore = useCallback(async () => {
    if (readMockMembersFromUrl()) return;
    if (loginStatus != 1 || loading || loadingMore || loadingMoreLock.current) return;
    if (!hasMore) return;
    const cursorId = rows[rows.length - 1]?.cursorId;
    if (!cursorId) return;

    loadingMoreLock.current = true;
    setLoadingMore(true);
    try {
      const res = await get_inviteMembersV2({ params: buildParams(cursorId) });
      const { items: list, hasNext } = pickRfapiPagedList(res);
      setHasMore(hasNext);
      if (!list.length) return;
      const mapped = mapItems(list);
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.key));
        const merged = [...prev];
        for (const row of mapped) {
          if (!seen.has(row.key)) {
            seen.add(row.key);
            merged.push(row);
          }
        }
        return merged;
      });
    } catch {
      // 静默失败，避免打断浏览
    } finally {
      loadingMoreLock.current = false;
      setLoadingMore(false);
    }
  }, [loginStatus, loading, loadingMore, hasMore, rows, buildParams]);

  loadMoreRef.current = loadMore;

  /** 表格内滚动到底 / 列表过短露底：用 IntersectionObserver 触发「滑动分页」加载下一页 */
  useEffect(() => {
    const root = tableBodyRef.current;
    const sentinel = scrollSentinelRef.current;
    if (!root || !sentinel) return;
    if (!hasMore || loading) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (loadingMoreLock.current) return;
        loadMoreRef.current();
      },
      { root, rootMargin: `${SCROLL_LOAD_EDGE}px`, threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, rows.length]);

  const flushSearchFromInput = useCallback(() => {
    setSearchKey(searchInput.trim());
  }, [searchInput]);

  const levelOptions = useMemo(
    () => [
      { value: "all", label: t("home.referralMembersAllLevels") },
      ...vipLevels.map((item) => ({
        value: String(item?.vipLevel ?? ""),
        label: `LEVEL ${item?.vipLevel ?? ""}`,
      })),
    ],
    [t, vipLevels]
  );

  const fmtRebate = (v) => `${Big(v ?? 0).toFixedMaxCy(2)} U`;

  if (loginStatus != 1) {
    return (
      <div className={styles.wrap}>
        <div className={styles.loginHint}>
          <a href={getUrl("/accounts/login" + loginQuery)} className={styles.loginLink}>
            {t("header.login")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <Select
          className={styles.levelSelect}
          dropdownClassName={styles.levelSelectPopup}
          bordered={false}
          value={levelFilter}
          options={levelOptions}
          onChange={setLevelFilter}
          getPopupContainer={(n) => n?.parentElement || document.body}
        />
        <Input
          className={styles.searchInput}
          allowClear
          placeholder={t("home.referralMembersEnterId")}
          prefix={<SearchIcon />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={flushSearchFromInput}
        />
      </div>

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loadingBox}>
            <Spin size="small" />
          </div>
        ) : (
          <div className={styles.tableScroll} ref={tableBodyRef}>
            <table className={styles.membersTable}>
              <thead>
                <tr>
                  <th scope="col" className={styles.th}>
                    {t("home.referralMembersColUserId")}
                  </th>
                  <th scope="col" className={styles.th}>
                    {t("home.referralMembersColSuperior")}
                  </th>
                  <th scope="col" className={styles.th}>
                    {t("home.referralMembersColLevel")}
                  </th>
                  <th scope="col" className={styles.thEnd}>
                    {t("home.referralMembersColTotalRebate")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={styles.emptyCell}>
                      {t("home.referralMembersEmpty")}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr key={row.key} className={i % 2 === 1 ? styles.tr : styles.trAlt}>
                      <td className={styles.tdUser}>{row.userId}</td>
                      <td className={styles.td}>{row.superiorLabel}</td>
                      <td className={styles.td}>
                        <span className={styles.levelPill}>
                          {t("home.referralMembersLevelBadge").replace("{level}", String(row.levelNum))}
                        </span>
                      </td>
                      <td className={`${styles.td} ${styles.tdRebate}`}>{fmtRebate(row.rebate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {rows.length > 0 && (
              <>
                <div ref={scrollSentinelRef} className={styles.scrollSentinel} aria-hidden />
                {loadingMore && (
                  <div className={styles.loadMoreRow}>
                    <Spin size="small" />
                  </div>
                )}
                {!loadingMore && hasMore && (
                  <div className={styles.loadMoreHint}>{t("home.referralMembersScrollMore")}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {t("home.referralMembersFooter").replace("{count}", String(rows.length))}
      </div>
    </div>
  );
}
